/**
 * customsirge_autofit.js — evens out product photos that were shot with different margins.
 *
 * The site shows every product image whole (object-fit: contain), so a belt photographed with a
 * wide white border looks small next to one shot tight. This reads each image once (downscaled to
 * 64px on a canvas; the Shopify CDN allows cross-origin reads), finds the bounding box of the
 * non-background pixels, and scales/translates the image so that box fills a consistent share of
 * its container. Photos that already fill their frame, lifestyle shots, and anything the canvas
 * cannot read are left exactly as they were.
 *
 * Hook: put `data-cs-autofit` on the IMG, or on a container that holds one IMG. Swatch tiles that
 * use a background-image get `data-cs-autofit-bg` on the element itself.
 * Tunables: data-cs-autofit="0.86" sets the share of the box the content should fill (default 0.86).
 */
(function () {
  'use strict';
  if (!('IntersectionObserver' in window) || !document.createElement('canvas').getContext) return;

  var SAMPLE = 64;      // analysis resolution
  var MAX_SCALE = 2.4;  // never blow a tiny product up more than this
  var cache = {};       // src -> bbox fractions

  function bgSample(d, w, h) {
    // average of the four corners = background guess (white for cut-outs; anything for lifestyle)
    var pts = [0, (w - 1) * 4, (h - 1) * w * 4, ((h - 1) * w + (w - 1)) * 4], r = 0, g = 0, b = 0, a = 0;
    for (var i = 0; i < 4; i++) { r += d[pts[i]]; g += d[pts[i] + 1]; b += d[pts[i] + 2]; a += d[pts[i] + 3]; }
    return [r / 4, g / 4, b / 4, a / 4];
  }

  function analyze(src) {
    if (cache[src]) return cache[src];
    var p = new Promise(function (resolve) {
      var im = new Image();
      im.crossOrigin = 'anonymous';
      im.onload = function () {
        try {
          var w = SAMPLE, h = Math.max(8, Math.round(SAMPLE * im.naturalHeight / im.naturalWidth));
          var c = document.createElement('canvas'); c.width = w; c.height = h;
          var ctx = c.getContext('2d', { willReadFrequently: true });
          ctx.drawImage(im, 0, 0, w, h);
          var d = ctx.getImageData(0, 0, w, h).data, bg = bgSample(d, w, h);
          var transparentBg = bg[3] < 30;
          var x0 = w, y0 = h, x1 = -1, y1 = -1;
          for (var y = 0; y < h; y++) for (var x = 0; x < w; x++) {
            var i = (y * w + x) * 4, isBg;
            if (transparentBg) isBg = d[i + 3] < 30;
            else isBg = d[i + 3] < 30 || (Math.abs(d[i] - bg[0]) + Math.abs(d[i + 1] - bg[1]) + Math.abs(d[i + 2] - bg[2])) < 60;
            if (!isBg) { if (x < x0) x0 = x; if (x > x1) x1 = x; if (y < y0) y0 = y; if (y > y1) y1 = y; }
          }
          if (x1 < 0) return resolve(null);
          var box = { x0: x0 / w, x1: (x1 + 1) / w, y0: y0 / h, y1: (y1 + 1) / h };
          // already fills the frame (lifestyle shot or tight crop): leave it
          if ((box.x1 - box.x0) > 0.93 && (box.y1 - box.y0) > 0.93) return resolve(null);
          resolve(box);
        } catch (e) { resolve(null); }
      };
      im.onerror = function () { resolve(null); };
      im.src = src;
    });
    cache[src] = p;
    return p;
  }

  function smallSrc(url) {
    // ask the CDN for a small rendition for analysis
    try {
      var u = new URL(url, location.href);
      if (/cdn\/shop/.test(u.pathname)) { u.searchParams.set('width', '160'); return u.toString(); }
    } catch (e) {}
    return url;
  }

  function fitImg(img) {
    if (img.dataset.csAutofitDone) return;
    var src = img.currentSrc || img.src;
    if (!src || !img.naturalWidth) return;
    img.dataset.csAutofitDone = '1';
    var fill = parseFloat(img.closest('[data-cs-autofit]') && img.closest('[data-cs-autofit]').getAttribute('data-cs-autofit')) || 0.86;
    analyze(smallSrc(src)).then(function (box) {
      if (!box) return;
      var bw = img.clientWidth, bh = img.clientHeight, iw = img.naturalWidth, ih = img.naturalHeight;
      if (!bw || !bh) return;
      var s = Math.min(bw / iw, bh / ih), dw = iw * s, dh = ih * s;       // object-fit: contain geometry
      var cw = (box.x1 - box.x0) * dw, ch = (box.y1 - box.y0) * dh;
      var scale = Math.min(fill * bw / cw, fill * bh / ch);
      scale = Math.max(1, Math.min(MAX_SCALE, scale));
      var cx = ((box.x0 + box.x1) / 2 - 0.5) * dw, cy = ((box.y0 + box.y1) / 2 - 0.5) * dh;
      img.style.transformOrigin = '50% 50%';
      img.style.transform = 'translate(' + (-cx * scale).toFixed(1) + 'px,' + (-cy * scale).toFixed(1) + 'px) scale(' + scale.toFixed(3) + ')';
      img.style.willChange = 'auto';
    });
  }

  function fitBg(el) {
    if (el.dataset.csAutofitDone) return;
    var m = /url\(["']?([^"')]+)["']?\)/.exec(getComputedStyle(el).backgroundImage);
    if (!m) return;
    el.dataset.csAutofitDone = '1';
    var fill = parseFloat(el.getAttribute('data-cs-autofit-bg')) || 0.9;
    var probe = new Image(); probe.crossOrigin = 'anonymous';
    probe.onload = function () {
      analyze(smallSrc(m[1])).then(function (box) {
        if (!box) return;
        var bw = el.clientWidth, bh = el.clientHeight, iw = probe.naturalWidth, ih = probe.naturalHeight;
        var s = Math.min(bw / iw, bh / ih), dw = iw * s, dh = ih * s;
        var cw = (box.x1 - box.x0) * dw, ch = (box.y1 - box.y0) * dh;
        var scale = Math.max(1, Math.min(MAX_SCALE, Math.min(fill * bw / cw, fill * bh / ch)));
        var cx = ((box.x0 + box.x1) / 2 - 0.5) * dw * scale, cy = ((box.y0 + box.y1) / 2 - 0.5) * dh * scale;
        el.style.backgroundSize = (dw * scale).toFixed(1) + 'px ' + (dh * scale).toFixed(1) + 'px';
        el.style.backgroundPosition = 'calc(50% - ' + cx.toFixed(1) + 'px) calc(50% - ' + cy.toFixed(1) + 'px)';
      });
    };
    probe.src = m[1];
  }

  function handle(el) {
    if (el.hasAttribute('data-cs-autofit-bg')) { fitBg(el); return; }
    var imgs = el.tagName === 'IMG' ? [el] : el.querySelectorAll('img');
    Array.prototype.forEach.call(imgs, function (img) {
      if (img.complete && img.naturalWidth) fitImg(img);
      else img.addEventListener('load', function () { fitImg(img); }, { once: true });
    });
  }

  var io = new IntersectionObserver(function (entries) {
    entries.forEach(function (e) { if (e.isIntersecting) { io.unobserve(e.target); handle(e.target); } });
  }, { rootMargin: '200px 0px' });

  function scan(root) {
    (root.querySelectorAll ? root.querySelectorAll('[data-cs-autofit], [data-cs-autofit-bg]') : []).forEach
      ? root.querySelectorAll('[data-cs-autofit], [data-cs-autofit-bg]').forEach(function (el) { io.observe(el); })
      : null;
    if (root.matches && root.matches('[data-cs-autofit], [data-cs-autofit-bg]')) io.observe(root);
  }

  function start() {
    scan(document);
    if ('MutationObserver' in window) {
      new MutationObserver(function (muts) {
        muts.forEach(function (m) { Array.prototype.forEach.call(m.addedNodes, function (n) { if (n.nodeType === 1) scan(n); }); });
      }).observe(document.body, { childList: true, subtree: true });
    }
    document.addEventListener('shopify:section:load', function (e) { scan(e.target); });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start); else start();
})();
